import { apiHttpService } from './apiHttpService';
import { Space, SpaceMember } from '../features/Spaces/spaces';
import { ListResponse } from '../domains/app';
import {
  BillingUnit,
  CurrentPlan,
  SubcriptionPlan,
} from '../features/Settings/settings';

export const fetchListSpaces = (
  pageSize: number,
  page: number,
  search?: string,
): Promise<ListResponse<Space>> => {
  return apiHttpService
    .get(
      `/api/Space?pageSize=${pageSize}&page=${page}${
        search ? `&searchValue=${search}` : ``
      }`,
    )
    .then((response) => response.data);
};

export const createSpace = (space: Space): Promise<Space> => {
  return apiHttpService
    .post(`/api/Space`, space)
    .then((response) => response.data);
};

export const fetchListMembers = (
  id: string,
  pageSize: number,
  page: number,
  search?: string,
): Promise<ListResponse<SpaceMember>> => {
  return apiHttpService
    .get(
      `/api/Space/${id}/member?pageSize=${pageSize}&page=${page}${
        search ? `&searchValue=${search}` : ``
      }`,
    )
    .then((response) => {
      response.data.items.forEach(
        (it: SpaceMember) =>
          (it.person.fullName = `${it.person.firstName} ${it.person.lastName}`),
      );
      return response.data;
    });
};

export const fetchListAdmins = (
  id: string,
  pageSize: number,
  page: number,
  search?: string,
): Promise<SpaceMember[]> => {
  return apiHttpService
    .get(
      `/api/Space/${id}/administrator?pageSize=${pageSize}&page=${page}${
        search ? `&searchValue=${search}` : ``
      }`,
    )
    .then((response) => {
      response.data.forEach(
        (it: SpaceMember) =>
          (it.person.fullName = `${it.person.firstName} ${it.person.lastName}`),
      );
      return response.data;
    });
};

export const updateSpaceName = (id: string, name: string): Promise<Space> => {
  return apiHttpService
    .put(`/api/Space/${id}/rename?newName=${name}`)
    .then((response) => response.data);
};

export const changeOwner = (
  id: string,
  oldOwnerId: string,
  newOwnerId: string,
): Promise<Space> => {
  const params = new URLSearchParams({ oldOwnerId, newOwnerId }).toString();
  return apiHttpService
    .post(`/api/Space/${id}/changeowner?${params}`)
    .then((response) => response.data);
};

export const deleteSpace = (id: string): Promise<Space> => {
  return apiHttpService
    .delete(`/api/Space/${id}`)
    .then((response) => response.data);
};

export const inviteMember = (
  spaceId: string,
  userId: string,
  email: string,
): Promise<any> => {
  return apiHttpService
    .post(
      `/api/Space/${spaceId}/invite/member?invitedByPersonId=${userId}&email=${encodeURIComponent(
        email,
      )}`,
    )
    .then((response) => response.data);
};

export const inviteAdministrator = (
  spaceId: string,
  userId: string,
  email: string,
): Promise<any> => {
  return apiHttpService
    .post(
      `/api/Space/${spaceId}/invite/administrator?invitedByPersonId=${userId}&email=${encodeURIComponent(
        email,
      )}`,
    )
    .then((response) => response.data);
};

export const deleteInvitation = (invitationId: string): Promise<any> => {
  return apiHttpService
    .delete(`/api/Space/invite?invitationId=${invitationId}`)
    .then((response) => response.data);
};

export const fetchListAdminInvitations = (
  spaceId: string,
  pageSize: number,
  page: number,
): Promise<ListResponse<any>> => {
  return apiHttpService
    .get(
      `/api/Space/${spaceId}/invite/administrator?pageSize=${pageSize}&page=${page}`,
    )
    .then((response) => response.data);
};
export const fetchListMemberInvitations = (
  spaceId: string,
  pageSize: number,
  page: number,
): Promise<ListResponse<any>> => {
  return apiHttpService
    .get(
      `/api/Space/${spaceId}/invite/member?pageSize=${pageSize}&page=${page}`,
    )
    .then((response) => response.data);
};

export const fetchListSubscriptions = (): Promise<SubcriptionPlan[]> => {
  return apiHttpService
    .get(`/api/Subscription/manage/plans`)
    .then((response) => response.data);
};

export const fetchCurrentSubscription = (
  spaceId: string,
): Promise<CurrentPlan> => {
  return apiHttpService
    .get(`/api/Subscription/space/${spaceId}`)
    .then((response) => response.data);
};

export const updateSubscription = (
  newSubscriptionPlanId: string,
  spaceId: string,
  billingUnit: BillingUnit,
  subscriptionId?: string,
  orderId?: string,
  paymentId?: string,
  billingToken?: string,
): Promise<any> => {
  return apiHttpService
    .post(
      `/api/Subscription/space/${spaceId}/plan/${newSubscriptionPlanId}?billingUnit=${billingUnit}${
        subscriptionId ? `&subscriptionId=${subscriptionId}` : ''
      }${orderId ? `&orderId=${orderId}` : ''}${
        paymentId ? `&paymentId=${paymentId}` : ''
      }${billingToken ? `&billingToken=${billingToken}` : ''}`,
    )
    .then((response) => response.data);
};

export const fetchMyUserInvitations = (
  email: string,
  pageSize: number,
  page: number,
): Promise<ListResponse<any>> => {
  return apiHttpService
    .get(
      `/api/Space/invite/member?pageSize=${pageSize}&page=${page}&email=${encodeURIComponent(
        email,
      )}`,
    )
    .then((response) => response.data);
};

export const fetchMyAdminInvitations = (
  email: string,
  pageSize: number,
  page: number,
): Promise<ListResponse<any>> => {
  return apiHttpService
    .get(
      `/api/Space/invite/administrator?pageSize=${pageSize}&page=${page}&email=${encodeURIComponent(
        email,
      )}`,
    )
    .then((response) => response.data);
};

export const acceptUserInvitation = (
  invitationId: string,
  personId: string,
): Promise<ListResponse<any>> => {
  return apiHttpService
    .post(
      `/api/Space/invite/member/accept?invitationId=${invitationId}&personId=${personId}`,
    )
    .then((response) => response.data);
};
export const acceptAdminInvitation = (
  invitationId: string,
  personId: string,
): Promise<ListResponse<any>> => {
  return apiHttpService
    .post(
      `/api/Space/invite/administrator/accept?invitationId=${invitationId}&personId=${personId}`,
    )
    .then((response) => response.data);
};

export const declineInvitation = (
  invitationId: string,
): Promise<ListResponse<any>> => {
  return apiHttpService
    .post(`/api/Space/invite/decline?invitationId=${invitationId}`)
    .then((response) => response.data);
};

export const getSpaceMembership = (personId: string): Promise<any[]> => {
  return apiHttpService
    .get(`/api/Space/member/${personId}`)
    .then((response) => response.data);
};

export const leaveFromSpace = (id: string, personId: string): Promise<any> => {
  return apiHttpService
    .post(`/api/Space/${id}/member/cancel?personId=${personId}`)
    .then((response) => response.data);
};

export const cancelSubscription = (spaceId: string): Promise<any> => {
  return apiHttpService
    .post(`/api/Subscription/space/${spaceId}/cancel`)
    .then((response) => response.data);
};

export const getBillingHistory = (spaceId: string): Promise<any[]> => {
  return apiHttpService
    .get(`/api/Subscription/space/${spaceId}/billing/history`)
    .then((response) => response.data);
};

export const getSubscriptionInfo = (subscriptionId: string): Promise<any> => {
  return apiHttpService
    .get(`/api/PayPal/subscriptions/${subscriptionId}`)
    .then((response) => response.data);
};

export const reviseSubscription = (
  subscriptionId: string,
  planId: string,
): Promise<any> => {
  return apiHttpService
    .post(
      `/api/PayPal/subscriptions/${subscriptionId}/revise?newPlanId=${planId}`,
    )
    .then((response) => response.data);
};

// eslint-disable-next-line
export default {
  fetchListSpaces,
  createSpace,
  fetchListMembers,
  fetchListAdmins,
  updateSpaceName,
  changeOwner,
  deleteSpace,
  inviteMember,
  inviteAdministrator,
  fetchListAdminInvitations,
  fetchListMemberInvitations,
  fetchListSubscriptions,
  updateSubscription,
  fetchMyUserInvitations,
  fetchMyAdminInvitations,
  acceptUserInvitation,
  acceptAdminInvitation,
  declineInvitation,
  getSpaceMembership,
  leaveFromSpace,
  fetchCurrentSubscription,
  getBillingHistory,
  cancelSubscription,
  getSubscriptionInfo,
  reviseSubscription,
  deleteInvitation,
};
